import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, RefreshCw, Search, Store, Star, ImageOff, AlertTriangle, Upload } from "lucide-react";
import { api, publicAssetUrl, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import BrandEditorDrawer from "@/components/admin/BrandEditorDrawer";
import BulkLogoUploadDialog from "@/components/admin/BulkLogoUploadDialog";

const DISTRICT_NAMES = [
    "Thiruvananthapuram", "Kollam", "Pathanamthitta", "Alappuzha", "Kottayam",
    "Idukki", "Ernakulam", "Thrissur", "Palakkad", "Malappuram", "Kozhikode",
    "Wayanad", "Kannur", "Kasaragod",
];

const CATEGORY_SLUGS = [
    "supermarkets", "hypermarkets", "fashion", "footwear", "electronics",
    "jewellery", "restaurants", "bakeries", "movies", "shopping-malls",
    "automobile", "healthcare", "beauty", "fitness", "books", "furniture",
    "hotels_resorts", "travel_tours",
];

const FILTERS = [
    { key: "all", label: "All" },
    { key: "featured", label: "Featured" },
];

/** Small square logo tile — falls back to an icon if `logo_url` is empty
 *  or fails to load. Uses a soft cream background so light logos remain
 *  visible without adding a border on the image itself. */
function BrandLogo({ url, name }) {
    const [errored, setErrored] = useState(false);
    if (!url || errored) {
        return (
            <div
                aria-label={`${name} logo placeholder`}
                className="flex h-20 w-20 items-center justify-center rounded-2xl bg-[#FFF3E6]"
            >
                <ImageOff size={26} className="text-[var(--brand-orange)]" />
            </div>
        );
    }
    return (
        <img
            src={publicAssetUrl(url)}
            alt={`${name} logo`}
            className="h-20 w-20 rounded-2xl object-contain bg-white ring-1 ring-slate-100"
            onError={() => setErrored(true)}
        />
    );
}

function districtCountLabel(branches) {
    const n = branches?.length || 0;
    const districts = new Set((branches || []).map((b) => b.district_slug));
    if (!n) return "No branches yet";
    return `${n} branch${n === 1 ? "" : "es"} · ${districts.size} district${districts.size === 1 ? "" : "s"}`;
}

export default function AdminPartnerShopsPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all"); // all | featured
    const [categoryFilter, setCategoryFilter] = useState("");
    const [q, setQ] = useState("");
    const [debouncedQ, setDebouncedQ] = useState("");

    // Drawer state ---------------------------------------------------------
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editing, setEditing] = useState(null); // null=create, object=edit

    // "Missing logo" sort mode — puts null-logo rows first when true.
    const [missingFirst, setMissingFirst] = useState(true);

    // Bulk logo upload dialog
    const [bulkOpen, setBulkOpen] = useState(false);

    // Optimistic featured-toggle rollback tracking.
    const pendingToggles = useRef(new Map());

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (filter === "featured") params.is_featured = true;
            if (categoryFilter) params.category_slug = categoryFilter;
            if (debouncedQ.trim()) params.q = debouncedQ.trim();
            const res = await api.get("/admin/partner-shops", { params });
            setItems(res.data.items || []);
            setTotal(res.data.total || 0);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    }, [filter, categoryFilter, debouncedQ]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(q), 250);
        return () => clearTimeout(t);
    }, [q]);

    useEffect(() => { load(); }, [load]);

    const onToggleFeatured = async (shop, next) => {
        // Optimistic update.
        setItems((prev) => prev.map((s) => (s.id === shop.id ? { ...s, is_featured: next } : s)));
        pendingToggles.current.set(shop.id, shop.is_featured);
        try {
            await api.patch(`/admin/partner-shops/${shop.id}`, { is_featured: next });
            toast.success(next ? `${shop.brand_name} pinned to top` : `${shop.brand_name} unpinned`);
            pendingToggles.current.delete(shop.id);
            // Refresh so featured-first ordering reflects new state.
            load();
        } catch (e) {
            // Rollback.
            const original = pendingToggles.current.get(shop.id);
            setItems((prev) => prev.map((s) => (s.id === shop.id ? { ...s, is_featured: original } : s)));
            pendingToggles.current.delete(shop.id);
            toast.error(errorMessage(e));
        }
    };

    const onDelete = async (shop) => {
        if (!window.confirm(`Delete "${shop.brand_name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/partner-shops/${shop.id}`);
            toast.success(`${shop.brand_name} removed`);
            load();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    const openCreate = () => {
        setEditing(null);
        setDrawerOpen(true);
    };
    const openEdit = (shop) => {
        setEditing(shop);
        setDrawerOpen(true);
    };
    const onSaved = () => {
        setDrawerOpen(false);
        load();
    };

    const featuredCount = useMemo(
        () => items.reduce((n, s) => n + (s.is_featured ? 1 : 0), 0),
        [items],
    );
    // A logo is "missing" when it's null, undefined, or an empty/whitespace string.
    const isMissingLogo = (s) => !s?.logo_url || String(s.logo_url).trim() === "";
    const missingLogoCount = useMemo(
        () => items.reduce((n, s) => n + (isMissingLogo(s) ? 1 : 0), 0),
        [items],
    );

    // Displayed list — optionally re-ordered so null-logo rows surface first
    // while preserving the server-returned order within each bucket.
    const displayed = useMemo(() => {
        if (!missingFirst || missingLogoCount === 0) return items;
        const missing = [];
        const rest = [];
        for (const s of items) (isMissingLogo(s) ? missing : rest).push(s);
        return [...missing, ...rest];
    }, [items, missingFirst, missingLogoCount]);

    return (
        <div className="space-y-6" data-testid="admin-partner-shops-page">
            <header>
                <div className="font-heading text-sm font-semibold text-[var(--brand-orange)]">Master Data</div>
                <h1 className="mt-1 font-heading text-3xl font-extrabold text-[var(--text-primary)] sm:text-4xl">
                    Partner Shops
                </h1>
                <p className="mt-1 max-w-2xl font-body text-sm text-[var(--text-muted)]">
                    Manage the Kerala master brand list. Featured brands appear at the top of customer views on both
                    web and mobile.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2 font-body text-xs text-[var(--text-muted)]">
                    <span className="status-pill status-pill-neutral" data-testid="partner-shop-total-count">
                        {total} brand{total === 1 ? "" : "s"}
                    </span>
                    <span className="status-pill" style={{ background: "#FFF3E6", color: "var(--brand-orange)" }}>
                        <Star size={12} className="mr-1" /> {featuredCount} featured
                    </span>
                    {missingLogoCount > 0 && (
                        <>
                            <span
                                className="inline-flex items-center gap-1 rounded-full bg-red-600 px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wide text-white shadow-sm ring-1 ring-red-700/40"
                                data-testid="partner-shop-missing-logo-count"
                            >
                                <AlertTriangle size={12} strokeWidth={2.5} />
                                {missingLogoCount} missing logo
                            </span>
                            <label
                                className="ml-1 inline-flex cursor-pointer select-none items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold text-[var(--text-primary)] shadow-sm hover:bg-slate-50"
                                data-testid="partner-shop-missing-first-toggle"
                            >
                                <input
                                    type="checkbox"
                                    checked={missingFirst}
                                    onChange={(e) => setMissingFirst(e.target.checked)}
                                    className="h-3.5 w-3.5 accent-[var(--brand-orange)]"
                                />
                                Show missing first
                            </label>
                        </>
                    )}
                </div>
            </header>

            {/* Toolbar */}
            <div className="card-soft flex flex-col gap-3 lg:flex-row lg:items-end">
                <div className="flex flex-1 flex-wrap gap-2">
                    {FILTERS.map((f) => (
                        <Button
                            key={f.key}
                            variant={filter === f.key ? "default" : "outline"}
                            onClick={() => setFilter(f.key)}
                            className={filter === f.key ? "btn-primary rounded-full" : "rounded-full border-slate-200"}
                            data-testid={`partner-shop-filter-${f.key}`}
                        >
                            {f.label}
                        </Button>
                    ))}
                    <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        data-testid="partner-shop-filter-category"
                        className="h-10 rounded-full border border-slate-200 bg-white px-4 font-body text-sm text-[var(--text-primary)] focus:border-[var(--brand-orange)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)]"
                    >
                        <option value="">All categories</option>
                        {CATEGORY_SLUGS.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                    <div className="relative min-w-[220px] flex-1">
                        <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                        <Input
                            placeholder="Search brand name or slug…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="partner-shop-search"
                            className="pl-9 rounded-full border-slate-200 focus:border-[var(--brand-orange)] focus-visible:ring-1 focus-visible:ring-[var(--brand-orange)]"
                        />
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setBulkOpen(true)}
                        data-testid="bulk-logo-upload-btn"
                        className="rounded-xl border-orange-300 text-orange-700 hover:bg-orange-50"
                    >
                        <Upload size={14} className="mr-2" /> Bulk upload logos
                    </Button>
                    <Button
                        variant="outline"
                        onClick={load}
                        disabled={loading}
                        data-testid="partner-shop-refresh-btn"
                        className="rounded-xl border-slate-200"
                    >
                        <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                    <Button
                        onClick={openCreate}
                        className="btn-primary rounded-xl"
                        data-testid="partner-shop-add-btn"
                    >
                        <Plus size={14} className="mr-2" /> Add Brand
                    </Button>
                </div>
            </div>

            {/* Card grid — 1 col mobile, 2 col ≥md */}
            {items.length === 0 && !loading ? (
                <div className="card-soft flex flex-col items-center justify-center gap-3 py-16 text-center">
                    <Store size={32} className="text-[var(--brand-orange)]" />
                    <h2 className="font-heading text-lg font-bold text-[var(--text-primary)]">
                        No brands match those filters
                    </h2>
                    <p className="max-w-md font-body text-sm text-[var(--text-muted)]">
                        Try clearing the filters, or add a new brand to start populating the master list.
                    </p>
                </div>
            ) : (
                <div
                    className="grid grid-cols-1 gap-4 md:grid-cols-2"
                    data-testid="partner-shop-grid"
                >
                    {displayed.map((shop) => (
                        <div
                            key={shop.id}
                            className="card-soft relative flex flex-col gap-4"
                            data-testid={`partner-shop-card-${shop.slug}`}
                        >
                            {/* Featured toggle in top-right */}
                            <div className="absolute right-4 top-4 flex items-center gap-2">
                                <span className="hidden font-heading text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] sm:inline">
                                    Featured
                                </span>
                                <Switch
                                    checked={!!shop.is_featured}
                                    onCheckedChange={(v) => onToggleFeatured(shop, v)}
                                    data-testid={`partner-shop-featured-toggle-${shop.slug}`}
                                    className="data-[state=checked]:bg-[var(--brand-orange)]"
                                />
                            </div>

                            <div className="flex items-start gap-4 pr-16">
                                <BrandLogo url={shop.logo_url} name={shop.brand_name} />
                                <div className="min-w-0 flex-1">
                                    <h3
                                        className="font-heading text-lg font-bold leading-tight text-[var(--text-primary)]"
                                        data-testid={`partner-shop-name-${shop.slug}`}
                                    >
                                        {shop.brand_name}
                                    </h3>
                                    {shop.tagline && (
                                        <p className="mt-0.5 font-body text-xs text-[var(--text-muted)] line-clamp-2">
                                            {shop.tagline}
                                        </p>
                                    )}
                                    <div className="mt-2 flex flex-wrap items-center gap-2">
                                        <span
                                            className="status-pill status-pill-neutral"
                                            data-testid={`partner-shop-category-${shop.slug}`}
                                        >
                                            {shop.category_slug}
                                        </span>
                                        {isMissingLogo(shop) && (
                                            <span
                                                className="inline-flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white shadow-sm ring-1 ring-red-700/40"
                                                data-testid={`partner-shop-missing-logo-${shop.slug}`}
                                            >
                                                <AlertTriangle size={11} strokeWidth={2.5} /> Missing logo
                                            </span>
                                        )}
                                        <span className="font-body text-xs text-[var(--text-muted)]">
                                            {districtCountLabel(shop.branches)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-auto flex items-center justify-between gap-2 border-t border-[var(--border-soft)] pt-3">
                                <button
                                    onClick={() => onDelete(shop)}
                                    className="font-heading text-xs font-semibold text-rose-500 hover:text-rose-600"
                                    data-testid={`partner-shop-delete-btn-${shop.slug}`}
                                >
                                    Delete
                                </button>
                                <Button
                                    variant="outline"
                                    onClick={() => openEdit(shop)}
                                    className="btn-secondary rounded-xl border-slate-200"
                                    data-testid={`partner-shop-manage-btn-${shop.slug}`}
                                >
                                    Manage
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <BrandEditorDrawer
                open={drawerOpen}
                shop={editing}
                categorySlugs={CATEGORY_SLUGS}
                districtNames={DISTRICT_NAMES}
                onClose={() => setDrawerOpen(false)}
                onSaved={onSaved}
            />
            <BulkLogoUploadDialog
                open={bulkOpen}
                onOpenChange={setBulkOpen}
                shops={items}
                onDone={load}
            />
        </div>
    );
}
